// src/components/modals/AddProductInventoryModal.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { X, AlertCircle, Package, Filter, Search, AlertTriangle } from 'lucide-react'
import ProductInventoryService from '../../services/productInventoryService'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'
import { PRODUCT_TYPE_CONFIG } from '../../utils/productModel'
import { isManagerRole } from '../../config/roles'

const AddProductInventoryModal = ({ isOpen, onClose, onInventoryCreated }) => {
  const [formData, setFormData] = useState({
    branch: '',
    selectedProducts: []
  })
  const [branches, setBranches] = useState([])
  const [availableProducts, setAvailableProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [errors, setErrors] = useState({})
  const [selectAll, setSelectAll] = useState(false)
  
  // New filter states
  const [productTypeFilter, setProductTypeFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [hasLoadedBranches, setHasLoadedBranches] = useState(false)

  // Apply product filters
  const applyFilters = useCallback(() => {
    let filtered = [...availableProducts]

    // Filter by product type
    if (productTypeFilter !== 'all') {
      filtered = filtered.filter(product => product.type === productTypeFilter)
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(product => 
        product.name?.toLowerCase().includes(search) ||
        product.sku?.toLowerCase().includes(search) ||
        product.categoryDetails?.name?.toLowerCase().includes(search)
      )
    }

    setFilteredProducts(filtered)
  }, [availableProducts, productTypeFilter, searchTerm])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen && !hasLoadedBranches) {
      const loadBranches = async () => {
        try {
          const currentUser = AuthService.getCurrentUser()
          
          // Reset form state
          setFormData({
            branch: '',
            selectedProducts: []
          })
          setErrors({})
          setSelectAll(false)
          setProductTypeFilter('all')
          setSearchTerm('')
          
          // Check if user is a manager
          if (isManagerRole(currentUser?.role) && currentUser?.branchId) {
            console.log('🏢 Manager detected, fetching assigned branch:', currentUser.branchId)
            
            // Fetch only the manager's assigned branch
            const response = await BranchService.getBranchById(currentUser.branchId)
            if (response?.success) {
              const branchData = response.data?.branch || response.data
              setBranches([branchData])
              console.log('✅ Manager branch loaded:', branchData.name)
              
              // Auto-select the manager's branch
              setFormData(prev => ({
                ...prev,
                branch: branchData._id
              }))
            } else {
              console.error('⚠️ Failed to fetch manager branch:', response)
              setErrors({ general: 'Failed to load your assigned branch' })
            }
          } else {
            // For admin/super_admin, fetch all branches
            console.log('🏢 Admin detected, fetching all branches')
            const response = await BranchService.getAllBranches()
            if (response?.success) {
              const branchesData = response.data?.branches || []
              setBranches(branchesData.filter(branch => branch.isActive))
              console.log('✅ All branches loaded:', branchesData.length)
            }
          }
          
          setHasLoadedBranches(true)
        } catch (error) {
          console.error('❌ Error fetching branches:', error)
          setErrors({ general: 'Failed to load branches' })
        }
      }
      
      loadBranches()
    }
    
    // Reset hasLoadedBranches when modal closes
    if (!isOpen) {
      setHasLoadedBranches(false)
    }
  }, [isOpen, hasLoadedBranches])

  // Fetch available products when branch changes
  useEffect(() => {
    if (formData.branch) {
      fetchUnassignedProducts(formData.branch)
    } else {
      setAvailableProducts([])
      setFilteredProducts([])
    }
  }, [formData.branch])

  // Apply filters when products, type filter, or search term changes
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  // Fetch unassigned products for selected branch (already filters out added products)
  const fetchUnassignedProducts = async (branchId) => {
    try {
      setLoadingProducts(true)
      setErrors({})
      
      const response = await ProductInventoryService.getUnassignedProducts(branchId)
      
      if (response?.success) {
        const productsData = response.data?.results?.unassignedProducts || []
        console.log('📦 Unassigned products fetched:', productsData)
        setAvailableProducts(productsData)
        
        if (productsData.length === 0) {
          setErrors({ products: 'No unassigned products available for this branch' })
        }
      } else {
        setAvailableProducts([])
        setErrors({ products: 'Failed to load available products' })
      }
    } catch (error) {
      console.error('❌ Error fetching unassigned products:', error)
      setAvailableProducts([])
      setErrors({ products: error.message || 'Failed to load available products' })
    } finally {
      setLoadingProducts(false)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear errors when user makes changes
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Check if recipe-based product has a recipe
  const hasRecipe = (product) => {
    if (product.type !== 'recipe_based') return true
    return product.recipe && product.recipe !== null
  }

  // Check if product can be selected
  const canSelectProduct = (product) => {
    return hasRecipe(product)
  }

  // Handle product selection
  const handleProductToggle = (productId) => {
    const product = filteredProducts.find(p => p._id === productId)
    if (!canSelectProduct(product)) return
    
    setFormData(prev => {
      const isSelected = prev.selectedProducts.includes(productId)
      const newSelected = isSelected
        ? prev.selectedProducts.filter(id => id !== productId)
        : [...prev.selectedProducts, productId]
      
      return {
        ...prev,
        selectedProducts: newSelected
      }
    })
    
    // Clear product selection error
    if (errors.selectedProducts) {
      setErrors(prev => ({
        ...prev,
        selectedProducts: ''
      }))
    }
  }

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)
    
    const selectableProducts = filteredProducts.filter(canSelectProduct)
    
    setFormData(prev => ({
      ...prev,
      selectedProducts: newSelectAll ? selectableProducts.map(product => product._id) : []
    }))
    
    // Clear product selection error
    if (errors.selectedProducts) {
      setErrors(prev => ({
        ...prev,
        selectedProducts: ''
      }))
    }
  }

  // Clear filters
  const clearFilters = () => {
    setProductTypeFilter('all')
    setSearchTerm('')
  }

  // Get product type badge
  const getProductTypeBadge = (type) => {
    const config = PRODUCT_TYPE_CONFIG[type] || PRODUCT_TYPE_CONFIG.PRODUCT
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    )
  }

  // Get recipe status warning for recipe-based products
  const getRecipeWarning = (product) => {
    if (product.type !== 'recipe_based') return null
    
    if (!hasRecipe(product)) {
      return (
        <div className="flex items-center mt-1 text-xs text-amber-600">
          <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>Add recipe first</span>
        </div>
      )
    }
    
    return null
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.branch) {
      newErrors.branch = 'Please select a branch'
    }
    
    if (formData.selectedProducts.length === 0) {
      newErrors.selectedProducts = 'Please select at least one product'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      // Prepare data for bulk creation
      const bulkData = {
        branch: formData.branch,
        products: formData.selectedProducts.map(productId => ({
          product: productId
        }))
      }
      
      console.log('📤 Creating product inventories:', bulkData)
      
      const response = await ProductInventoryService.createMultipleInventories(bulkData)
      
      if (response.success) {
        console.log('✅ Product inventories created successfully:', response)
        
        // Get the created inventories from response
        const createdInventories = response.data?.results?.successful || []
        
        // Notify parent component
        onInventoryCreated(createdInventories.map(item => item.inventory))
        
        // Reset form
        setFormData({
          branch: '',
          selectedProducts: []
        })
        setErrors({})
        setSelectAll(false)
        setProductTypeFilter('all')
        setSearchTerm('')
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Creation failed with response:', response)
        setErrors({ general: response.message || 'Failed to create product inventories' })
      }
    } catch (error) {
      console.error('❌ Error creating product inventories:', error)
      setErrors({ general: error.message || 'Failed to create product inventories. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  // Close modal
  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="ml-4">
              <h3 className='text-lg leading-6 font-medium text-gray-900'>Add Products to Inventory</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* General Error */}
          {errors.general && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="mt-1 text-sm text-red-700">{errors.general}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-2">
              Branch *
            </label>
            <div className="relative">
              <select
                id="branch"
                name="branch"
                value={formData.branch}
                onChange={handleInputChange}
                disabled={loading || (branches.length === 1 && formData.branch)}
                className={`block w-full pl-3 pr-10 py-2 border rounded-md leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                  errors.branch ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select branch</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
            {errors.branch && (
              <p className="mt-1 text-sm text-red-600">{errors.branch}</p>
            )}
            {!formData.branch && (
              <p className="mt-1 text-xs text-gray-500">
                Please select a branch to view available products. Already added products are automatically hidden.
              </p>
            )}
          </div>

          {/* Products Selection */}
          {formData.branch && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Products *
                </label>
                {filteredProducts.filter(canSelectProduct).length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAllToggle}
                    className="text-sm text-orange-600 hover:text-orange-800"
                  >
                    {selectAll ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>

              {/* Filter Controls */}
              {availableProducts.length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Filter className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Filter Products</span>
                    </div>
                    {(productTypeFilter !== 'all' || searchTerm) && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="text-xs text-orange-600 hover:text-orange-800"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Search */}
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    {/* Product Type Filter */}
                    <select
                      value={productTypeFilter}
                      onChange={(e) => setProductTypeFilter(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="all">All Product Types</option>
                      <option value="product">📦 Regular Products</option>
                      <option value="recipe_based">🍳 Recipe-Based Products</option>
                    </select>
                  </div>

                  {/* Filter Results Info */}
                  <div className="mt-3 text-xs text-gray-600">
                    Showing {filteredProducts.length} of {availableProducts.length} products
                    {filteredProducts.filter(p => !canSelectProduct(p)).length > 0 && (
                      <span className="text-amber-600 ml-2">
                        ({filteredProducts.filter(p => !canSelectProduct(p)).length} require recipes)
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading products...</span>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {availableProducts.length === 0 ? 'No products available' : 'No products match your filters'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {availableProducts.length === 0 
                      ? 'All products have already been added to this branch or no products exist.'
                      : 'Try adjusting your search criteria or filters.'
                    }
                  </p>
                  {(productTypeFilter !== 'all' || searchTerm) && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-2 text-sm text-orange-600 hover:text-orange-800"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  {filteredProducts.map((product) => {
                    const isSelectable = canSelectProduct(product)
                    const isSelected = formData.selectedProducts.includes(product._id)
                    
                    return (
                      <div
                        key={product._id}
                        className={`flex items-center justify-between p-4 border-b border-gray-100 last:border-b-0 ${
                          isSelectable ? 'hover:bg-gray-50' : 'bg-gray-50'
                        } ${
                          isSelected ? 'bg-orange-50' : ''
                        } ${
                          !isSelectable ? 'opacity-75' : ''
                        }`}
                      >
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleProductToggle(product._id)}
                            disabled={!isSelectable}
                            className={`h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded ${
                              !isSelectable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`text-sm font-medium ${isSelectable ? 'text-gray-900' : 'text-gray-600'}`}>
                                  {product.name}
                                </p>
                                <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                                {product.categoryDetails && (
                                  <p className="text-xs text-gray-500">Category: {product.categoryDetails.name}</p>
                                )}
                                {getRecipeWarning(product)}
                              </div>
                              <div className="flex items-center space-x-2">
                                {getProductTypeBadge(product.type)}
                                <span className="text-sm font-semibold text-gray-900">
                                  LKR {product.outletPrice?.toLocaleString() || '0'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {errors.selectedProducts && (
                <p className="mt-2 text-sm text-red-600">{errors.selectedProducts}</p>
              )}
              {errors.products && (
                <p className="mt-2 text-sm text-red-600">{errors.products}</p>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {formData.selectedProducts.length > 0 && (
              <span>{formData.selectedProducts.length} product(s) selected</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || formData.selectedProducts.length === 0}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                `Add ${formData.selectedProducts.length} Product(s)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddProductInventoryModal